import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BarChart3, Search, X, Package, Calendar, ArrowUpFromLine, ArrowDownToLine, TrendingUp, TrendingDown, DollarSign, Store, Truck, PackageCheck, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StatisticsPage() {
  const [products, setProducts] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [period, setPeriod] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  
  // Product detail dialog state
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSalesHistory, setProductSalesHistory] = useState(null);
  const [loadingSalesHistory, setLoadingSalesHistory] = useState(false);
  
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchStatistics();
  }, [period]);

  const fetchStatistics = async (search = searchQuery) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("period", period);
      if (search) params.append("search", search);
      params.append("limit", "200");
      
      const response = await axios.get(`${API}/history/product-stats?${params.toString()}`);
      setProducts(response.data.products || []);
      setTotals(response.data.totals || null);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchStatistics(value);
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery("");
    fetchStatistics("");
  };

  const openProductDetail = async (product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
    setLoadingSalesHistory(true);
    
    try {
      const response = await axios.get(`${API}/history/product-sales/${product.ean}`);
      setProductSalesHistory(response.data);
    } catch (error) {
      console.error("Error fetching sales history:", error);
      setProductSalesHistory(null);
    } finally {
      setLoadingSalesHistory(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPeriodLabel = () => {
    switch (period) {
      case "today":
        return "Idag";
      case "week":
        return "Senaste 7 dagarna";
      case "month":
        return "Senaste 30 dagarna";
      default:
        return "All tid";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-primary" />
              <h1
                className="font-heading text-2xl font-bold uppercase tracking-tight"
                data-testid="statistics-title"
              >
                Statistik
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={period === "today" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("today")}
                data-testid="period-today"
              >
                Idag
              </Button>
              <Button
                variant={period === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("week")}
                data-testid="period-week"
              >
                7 dagar
              </Button>
              <Button
                variant={period === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("month")}
                data-testid="period-month"
              >
                30 dagar
              </Button>
              <Button
                variant={period === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("all")}
                data-testid="period-all"
              >
                Alla
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Summary Cards */}
      {totals && (
        <div className="border-b border-border bg-card/50">
          <div className="container mx-auto px-4 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card data-testid="stat-total-quantity">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <ArrowUpFromLine className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Totalt utskannade</p>
                      <p className="text-2xl font-bold">{totals.total_quantity_picked} st</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="stat-received">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <ArrowDownToLine className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Totalt inskannade</p>
                      <p className="text-2xl font-bold">{totals.total_quantity_received || 0} st</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="stat-sold">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <DollarSign className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Antal sålda</p>
                      <p className="text-2xl font-bold">{totals.total_sold || 0} st</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="stat-unique-products">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Package className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Unika produkter</p>
                      <p className="text-2xl font-bold">{totals.unique_products}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Search bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Sök EAN, artikelnummer eller produktnamn..."
            className="pl-10 pr-10"
            data-testid="statistics-search"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:flex">
              <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">Översikt</TabsTrigger>
              <TabsTrigger value="profitability" data-testid="tab-profitability" className="text-xs sm:text-sm">Lönsamhet</TabsTrigger>
              <TabsTrigger value="sales-type" data-testid="tab-sales-type" className="text-xs sm:text-sm">Per typ</TabsTrigger>
            </TabsList>
            <span className="text-sm text-muted-foreground text-center sm:text-right">
              {products.length} produkt{products.length !== 1 ? "er" : ""}
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Laddar statistik...
            </div>
          ) : products.length === 0 ? (
            <Card className="p-12 text-center" data-testid="empty-statistics">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-xl font-bold mb-2">
                {searchQuery ? "Inga träffar" : "Ingen statistik ännu"}
              </h3>
              <p className="text-muted-foreground">
                {searchQuery
                  ? "Prova ett annat sökord"
                  : "Statistik visas här när produkter har skannats"}
              </p>
            </Card>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              <TabsContent value="overview" className="space-y-4">
                <Card data-testid="statistics-table-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">EAN</TableHead>
                        <TableHead>Artikelnummer</TableHead>
                        <TableHead className="hidden md:table-cell">Beskrivning</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">Inleverans</TableHead>
                        <TableHead className="text-right">Utleverans</TableHead>
                        <TableHead className="text-right">Antal sålda</TableHead>
                        <TableHead className="text-right hidden lg:table-cell">Inköpspris</TableHead>
                        <TableHead className="text-right hidden lg:table-cell">Försäljningspris</TableHead>
                        <TableHead className="hidden xl:table-cell">Senast utleverans</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product, index) => {
                        // Use quantity_sold from backend (only sales with actual price)
                        const antalSalda = product.quantity_sold || 0;
                        
                        return (
                        <TableRow key={product.ean || index} data-testid={`stat-row-${product.ean}`}>
                          <TableCell className="font-mono text-sm">
                            {product.ean || "—"}
                          </TableCell>
                          <TableCell className="font-medium">
                            {product.artikelnummer || "—"}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">
                            {product.produktnamn || "—"}
                          </TableCell>
                          <TableCell className="text-right hidden sm:table-cell">
                            <span className="font-bold text-green-500">
                              {product.total_received || 0} st
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-bold text-red-500">
                              {product.total_picked} st
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {antalSalda > 0 ? (
                              <span className="font-bold text-purple-500">
                                {antalSalda} st
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right hidden lg:table-cell">
                            {product.purchase_price_sek ? (
                              <span className="font-medium">
                                {Math.round(product.purchase_price_sek).toLocaleString('sv-SE')} kr
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right hidden lg:table-cell">
                            {product.sales_price_sek ? (
                              <span className="font-medium text-blue-500">
                                {Math.round(product.sales_price_sek).toLocaleString('sv-SE')} kr
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                            {formatDate(product.last_picked)}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </TabsContent>

              {/* PROFITABILITY TAB */}
              <TabsContent value="profitability" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      Lönsamhet per produkt
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Artikel</TableHead>
                          <TableHead className="text-right">Antal sålda</TableHead>
                          <TableHead className="text-right hidden sm:table-cell">Inköpspris</TableHead>
                          <TableHead className="text-right hidden md:table-cell">Frakt</TableHead>
                          <TableHead className="text-right hidden md:table-cell">Kem. skatt</TableHead>
                          <TableHead className="text-right hidden sm:table-cell">Total kostnad</TableHead>
                          <TableHead className="text-right">Försäljning</TableHead>
                          <TableHead className="text-right hidden sm:table-cell">Bruttovinst</TableHead>
                          <TableHead className="text-right">Marginal</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.filter(p => p.purchase_price_sek || p.sales_price_sek).map((product, index) => {
                          // Use quantity_sold from backend (only sales with actual price)
                          const antalSalda = product.quantity_sold || 0;
                          
                          return (
                          <TableRow 
                            key={product.ean || index} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => openProductDetail(product)}
                            data-testid={`profitability-row-${product.ean}`}
                          >
                            <TableCell>
                              <div>
                                <p className="font-mono font-medium">{product.artikelnummer || product.ean}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                  {product.produktnamn}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {antalSalda > 0 ? (
                                <span className="font-bold text-purple-500">{antalSalda} st</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right hidden sm:table-cell">
                              {product.purchase_price_sek ? (
                                `${Math.round(product.purchase_price_sek).toLocaleString('sv-SE')} kr`
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right hidden md:table-cell">
                              {product.freight_cost > 0 ? (
                                `${product.freight_cost.toLocaleString('sv-SE')} kr`
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right hidden md:table-cell">
                              {product.chemical_tax > 0 ? (
                                `${product.chemical_tax.toLocaleString('sv-SE')} kr`
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-medium hidden sm:table-cell">
                              {product.total_cost ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      {Math.round(product.total_cost).toLocaleString('sv-SE')} kr
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Inköp + Frakt + Kem. skatt</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right text-blue-500 font-medium">
                              {product.sales_price_sek ? (
                                `${Math.round(product.sales_price_sek).toLocaleString('sv-SE')} kr`
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right hidden sm:table-cell">
                              {product.gross_profit !== null ? (
                                <span className={product.gross_profit >= 0 ? "text-green-500 font-bold" : "text-red-500 font-bold"}>
                                  {product.gross_profit >= 0 ? "+" : ""}
                                  {Math.round(product.gross_profit).toLocaleString('sv-SE')} kr
                                </span>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {product.profit_margin !== null ? (
                                <Badge variant={product.profit_margin >= 20 ? "success" : product.profit_margin >= 0 ? "warning" : "destructive"}>
                                  {product.profit_margin.toFixed(1)}%
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* SALES TYPE TAB */}
              <TabsContent value="sales-type" className="space-y-4">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <PackageCheck className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Bring</p>
                          <p className="text-2xl font-bold">
                            {products.reduce((sum, p) => sum + (p.sold_via_bring || 0), 0)} st
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <Truck className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">DSV</p>
                          <p className="text-2xl font-bold">
                            {products.reduce((sum, p) => sum + (p.sold_via_dsv || 0), 0)} st
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-500/10 rounded-lg">
                          <Truck className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Egen runda</p>
                          <p className="text-2xl font-bold">
                            {products.reduce((sum, p) => sum + (p.sold_via_route || 0), 0)} st
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/10 rounded-lg">
                          <Store className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Hämtad i butik</p>
                          <p className="text-2xl font-bold">
                            {products.reduce((sum, p) => sum + (p.sold_via_store || 0), 0)} st
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Försäljning per typ och produkt
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Artikel</TableHead>
                          <TableHead className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <PackageCheck className="h-4 w-4 text-blue-500" />
                              Bring
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Truck className="h-4 w-4 text-green-500" />
                              DSV
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Truck className="h-4 w-4 text-orange-500" />
                              Runda
                            </div>
                          </TableHead>
                          <TableHead className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Store className="h-4 w-4 text-purple-500" />
                              Butik
                            </div>
                          </TableHead>
                          <TableHead className="text-right">Totalt ut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.filter(p => 
                          (p.sold_via_store || 0) + (p.sold_via_route || 0) + 
                          (p.sold_via_bring || 0) + (p.sold_via_dsv || 0) > 0
                        ).map((product, index) => (
                          <TableRow key={product.ean || index}>
                            <TableCell>
                              <div>
                                <p className="font-mono font-medium">{product.artikelnummer || product.ean}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">{product.produktnamn}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {product.sold_via_bring > 0 ? (
                                <Badge variant="outline" className="text-blue-500 border-blue-500/30">
                                  {product.sold_via_bring} st
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {product.sold_via_dsv > 0 ? (
                                <Badge variant="outline" className="text-green-500 border-green-500/30">
                                  {product.sold_via_dsv} st
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {product.sold_via_route > 0 ? (
                                <Badge variant="outline" className="text-orange-500 border-orange-500/30">
                                  {product.sold_via_route} st
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {product.sold_via_store > 0 ? (
                                <Badge variant="outline" className="text-purple-500 border-purple-500/30">
                                  {product.sold_via_store} st
                                </Badge>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-bold">
                              {product.total_picked} st
                            </TableCell>
                          </TableRow>
                        ))}
                        {products.filter(p => 
                          (p.sold_via_store || 0) + (p.sold_via_route || 0) + 
                          (p.sold_via_bring || 0) + (p.sold_via_dsv || 0) > 0
                        ).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              Ingen försäljning registrerad under denna period
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      {/* Product Sales Detail Dialog */}
      <Dialog open={showProductDetail} onOpenChange={setShowProductDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Försäljningshistorik
            </DialogTitle>
            {selectedProduct && (
              <DialogDescription>
                <span className="font-mono font-medium text-foreground">{selectedProduct.artikelnummer}</span>
                {selectedProduct.produktnamn && (
                  <span className="ml-2 text-muted-foreground">- {selectedProduct.produktnamn}</span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {loadingSalesHistory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : productSalesHistory ? (
            <div className="space-y-4">
              {/* Pricing Summary */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Inköpspris</p>
                      <p className="font-bold">{productSalesHistory.pricing.purchase_price_sek?.toLocaleString('sv-SE')} kr</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Frakt</p>
                      <p className="font-bold">{productSalesHistory.pricing.freight_cost?.toLocaleString('sv-SE') || 0} kr</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Kem. skatt</p>
                      <p className="font-bold">{productSalesHistory.pricing.chemical_tax?.toLocaleString('sv-SE') || 0} kr</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total kostnad/st</p>
                      <p className="font-bold text-orange-500">{productSalesHistory.pricing.total_cost_per_unit?.toLocaleString('sv-SE')} kr</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Antal sålda</p>
                    <p className="text-xl font-bold text-purple-500">{productSalesHistory.totals.total_quantity_sold} st</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total intäkt</p>
                    <p className="text-xl font-bold text-blue-500">
                      {productSalesHistory.totals.total_revenue?.toLocaleString('sv-SE') || "—"} kr
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total kostnad</p>
                    <p className="text-xl font-bold text-orange-500">
                      {productSalesHistory.totals.total_cost?.toLocaleString('sv-SE')} kr
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total vinst</p>
                    <p className={`text-xl font-bold ${productSalesHistory.totals.total_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {productSalesHistory.totals.total_profit !== null 
                        ? `${productSalesHistory.totals.total_profit >= 0 ? '+' : ''}${productSalesHistory.totals.total_profit?.toLocaleString('sv-SE')} kr`
                        : "—"
                      }
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Sales List */}
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Antal</TableHead>
                      <TableHead>Ordernr</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Inpris</TableHead>
                      <TableHead className="text-right">Utpris</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Vinst</TableHead>
                      <TableHead className="text-right">Marginal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productSalesHistory.sales.length > 0 ? (
                      productSalesHistory.sales.map((sale, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-sm">
                            {formatDate(sale.timestamp)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {sale.quantity} st
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {sale.order_number || "—"}
                          </TableCell>
                          <TableCell className="text-right hidden sm:table-cell">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="cursor-help">
                                  {sale.total_cost?.toLocaleString('sv-SE')} kr
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Inköp: {sale.purchase_price_sek?.toLocaleString('sv-SE')} kr</p>
                                  <p>Frakt: {sale.freight_cost?.toLocaleString('sv-SE')} kr</p>
                                  <p>Kem. skatt: {sale.chemical_tax?.toLocaleString('sv-SE')} kr</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell className="text-right text-blue-500 font-medium">
                            {sale.sales_price_incl_vat 
                              ? `${sale.sales_price_incl_vat.toLocaleString('sv-SE')} kr`
                              : <span className="text-muted-foreground">—</span>
                            }
                          </TableCell>
                          <TableCell className="text-right hidden sm:table-cell">
                            {sale.gross_profit !== null ? (
                              <span className={sale.gross_profit >= 0 ? "text-green-500 font-medium" : "text-red-500 font-medium"}>
                                {sale.gross_profit >= 0 ? "+" : ""}{sale.gross_profit?.toLocaleString('sv-SE')} kr
                              </span>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {sale.profit_margin !== null ? (
                              <Badge variant={sale.profit_margin >= 20 ? "success" : sale.profit_margin >= 0 ? "warning" : "destructive"}>
                                {sale.profit_margin?.toFixed(1)}%
                              </Badge>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Ingen försäljningshistorik hittad
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              Kunde inte hämta försäljningshistorik
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
